"use client";

import { useEffect, useRef } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { reportDataAdapter } from "@features/report/reportDataAdapter";
import type {
  BriefReportSummary,
  SummaryBarState,
} from "@features/report/reportDataAdapter";
import { useReportAdapterDeps } from "@features/report/useReportAdapterDeps";
import { raceTimeoutMs } from "@features/report/queryTimeout";
import { Track } from "@/src/lib/tracking";

/** 共享资源：S12 T12.1（report close）/ S13 消费（fe-spec §3.2 状态表） */
export const REPORT_SUMMARY_QUERY_KEY = ["report-summary"] as const;

/** fe-spec §2.4：摘要条取数 3s 硬约束（超时整条不渲染） */
export const REPORT_SUMMARY_TIMEOUT_MS = 3000;

/** 摘要条宿主渲染分派：loading → 骨架；visible → 组件；hidden → 不渲染 */
export type ReportSummaryBarStatus = "loading" | "visible" | "hidden";

export type ReportSummaryState = {
  summary: BriefReportSummary | undefined;
  /** 渲染分派（unread=0 / 失败 / 超时 → hidden；可见两态直取服务端 bar_state，前端不推导） */
  status: ReportSummaryBarStatus;
  barState: SummaryBarState;
  unreadCount: number;
  newCount: number;
};

export function useReportSummary(): ReportSummaryState {
  // 拼装期运行时依赖（trackerApi/ownerId/market 判定）；未就绪挂起（市场已读态入 key，markRead 后 invalidate 出新数字）
  const deps = useReportAdapterDeps();
  const query = useQuery({
    queryKey: [
      ...REPORT_SUMMARY_QUERY_KEY,
      deps?.ownerId ?? "",
      deps?.marketUnread ?? false,
      deps?.reportDate ?? "",
    ],
    enabled: Boolean(deps),
    // 3s 超时在 query 层统一（adapter 边界不带 abort 语义）；超时即失败、不重试
    queryFn: () =>
      raceTimeoutMs(
        reportDataAdapter.fetchSummary(deps!),
        REPORT_SUMMARY_TIMEOUT_MS,
        "report-summary",
      ),
    retry: false,
  });

  const { data, isPending, error } = query;
  const failedRef = useRef(false);
  /** 首屏 query 是否已 settle（settle 仅一次；invalidate refetch 不重置） */
  const firstSettledRef = useRef(false);

  // brief_summary_bar_fail：失败/超时一次性上报（§3.6；声明式无宿主 → effect）
  useEffect(() => {
    if (!error) {
      failedRef.current = false;
      return;
    }
    if (failedRef.current) {
      return;
    }
    failedRef.current = true;
    Track.error(error, { surface: "brief_summary_bar" });
    Track.event("brief_summary_bar_fail");
  }, [error]);

  const status: ReportSummaryBarStatus = isPending
    ? "loading"
    : !data || data.unread_count <= 0 || error
      ? "hidden"
      : "visible";

  // brief_summary_bar_empty：仅「首屏 query settle 即 hidden」（unread=0/失败/超时）上报一次；
  // 首屏 settle 后 visible→hidden 的 invalidate refetch 路径不报（refetch 期间 isPending=false，
  // loading 态只在首屏出现，故以 firstSettledRef 区分）
  useEffect(() => {
    if (status === "loading" || firstSettledRef.current) {
      return;
    }
    firstSettledRef.current = true;
    if (status === "hidden") {
      Track.event("brief_summary_bar_empty");
    }
  }, [status]);

  return {
    summary: data,
    status,
    barState: status === "visible" && data ? data.bar_state : "hidden",
    unreadCount: data?.unread_count ?? 0,
    newCount: data?.new_count ?? 0,
  };
}

/** S12 T12.1（report close）/ S13 消费：invalidate 后 refetch 按 A.1 兜底出新形态 */
export function invalidateReportSummary(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: REPORT_SUMMARY_QUERY_KEY });
}
